import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Box, Button, Dialog, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import CloseModalButton from "../../../../components/CloseModalButton";
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled";
import autoclaveImage from "../../../../images/items/autoclave.jpg";
import { AddEditAutoclaveErrors, Autoclave, AutoclaveType, OpenAutoclaveModal, SporetestingResult, ValidationField } from "../types";
import { useAppDispatch } from "../../../../redux/store";
import { createAutoclave, updateAutoclave } from "../../../../services/biologicalServices";
import { getAutoclaveFrequencies, getAutoclaveTypeName, updateSterilizationMethod } from "../utils/helpers";
import { addError, addSuccess } from "../../../../redux/alerts";
import useCustomerNumber from "../../../../hooks/useCustomerNumber";
import { AutoclaveTypeId } from "../enums";
import { Biobox } from "../../../../types";

interface AddEditAutoclaveProps {
  autoclaves: Autoclave[];
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>;
  bioboxes: Biobox[];
  sporetests: SporetestingResult[];
  openModal: OpenAutoclaveModal;
  setOpenModal: Dispatch<SetStateAction<OpenAutoclaveModal>>;
  reload: () => Promise<void>;
}

const initialFormState: Partial<Autoclave> = {
  autoclave_name: '',
  autoclave_serial: '',
  autoclavetype_id: 10,
  autoclave_brand: '',
  autoclave_model: '',
  autoclave_frequency: 10,
  customer_number: ''
}

const initialErrorState = {
  autoclave_name: {
    isValid: true,
    message: null,
  },
  autoclave_serial: {
    isValid: true,
    message: null,
  },
  autoclave_brand: {
    isValid: true,
    message: null,
  },
  autoclave_model: {
    isValid: true,
    message: null,
  },
}

const isValidationField = (field: any): field is ValidationField => {
  const validationFields = ['autoclave_name', 'autoclave_brand', 'autoclave_model', 'autoclave_serial'] as const
  return validationFields.includes(field)
}

const AddEditAutoclave = (props: AddEditAutoclaveProps) => {
  const { autoclaves, autoclaveTypeById, bioboxes, sporetests, openModal, setOpenModal, reload } = props

  const { autoclave } = openModal

  const [formData, setFormData] = useState<Partial<Autoclave>>(autoclave ?? initialFormState);
  const [formErrors, setFormErrors] = useState<AddEditAutoclaveErrors>(initialErrorState);
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const customerNumber = useCustomerNumber()

  const serialNumbers = useMemo(() => {
    const serials = new Set<string>()
    const currentSerial = autoclave?.autoclave_serial.trim().toLowerCase()

    autoclaves.forEach(autoclave => {
      const serialNumber = autoclave.autoclave_serial.trim().toLowerCase()
      serials.add(serialNumber)
    })

    if (currentSerial) {
      serials.delete(currentSerial)
    }

    return serials
  }, [autoclave, autoclaves])

  const autoclaveNames = useMemo(() => {
    const names = new Set<string>()
    const currentName = autoclave?.autoclave_name.trim().toLowerCase()

    autoclaves.forEach(autoclave => {
      const name = autoclave.autoclave_name.trim().toLowerCase()
      names.add(name)
    })

    if (currentName) {
      names.delete(currentName)
    }

    return names
  }, [autoclave, autoclaves])  

  const handleEditChange = (field: string, value: string | number) => {
    if (isValidationField(field)) {
      validateField(field, value as string)
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  }

  const hasChangedAutoclaveType = autoclave
    && Number(autoclave.autoclavetype_id) !== Number(formData.autoclavetype_id)

  const handleSave = async () => {
    setLoading(true);

    try {
      const payload: Partial<Autoclave> = {
        customer_number: customerNumber,
        autoclave_name: formData.autoclave_name?.trim(),
        autoclavetype_id: Number(formData.autoclavetype_id),
        autoclave_brand: formData.autoclave_brand?.trim(),
        autoclave_model: formData.autoclave_model?.trim(),
        autoclave_serial: formData.autoclave_serial?.trim(),
        autoclave_frequency: Number(formData.autoclave_frequency),
      }

      if (autoclave) {
        payload['autoclave_id'] = autoclave.autoclave_id

        await updateAutoclave(customerNumber, payload)

        if (formData.autoclavetype_id && hasChangedAutoclaveType) {
          const autoclaveBoxes = bioboxes.filter(box => {
            return box.box_type === 'mesa'
              && Number(box.autoclave_id) === Number(autoclave.autoclave_id)
          })

          for (const box of autoclaveBoxes) {
            const boxNumber = box.box_number

            const boxTests = sporetests.filter(test => {
              return test.test_type === 'mesa'
                && Number(test.box_id) === Number(box.box_id)
            })

            await updateSterilizationMethod(customerNumber, formData.autoclavetype_id, boxNumber, boxTests)
          }
        }
      } else {
        await createAutoclave(customerNumber, payload)        
      }

      const message = autoclave
        ? 'Autoclave was updated successfully'
        : 'Autoclave was added successfully'

      dispatch(addSuccess(message))

      setLoading(false);
    } catch (error) {
      console.error(error);

      const err = error as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage =
        err?.response?.data?.message || err?.message || 'There was an error saving the autoclave, please try again later'

      setLoading(false);
      dispatch(addError(errorMessage));
      return;
    }

    reload();
    handleClose();
  }

  const messagesByField = useMemo(() => {
    return new Map([
      ['autoclave_name', ['Autoclave Name is required', 'Autoclave Name is assigned to another autoclave']],
      ['autoclave_serial', ['Serial/Sterilizer # is required', 'Serial/Sterilizer # is assigned to another autoclave']],
      ['autoclave_brand', ['Brand is required']],
      ['autoclave_model', ['Model is required']],
    ])
  }, [])

  const getValidationMessage = (field: ValidationField, value: string) => {
    const messages = messagesByField.get(field) ?? null
    const hasValue = Boolean(value)

    if (hasValue) {
      if (field === 'autoclave_serial' && serialNumbers.has(value)) {
        return messages && messages[1]
      } else if (field === 'autoclave_name' && autoclaveNames.has(value)) {
        return messages && messages[1]
      } else {
        return null
      }
    } else {
      const message = messages && messages[0]
      return message
    }
  }

  const isValidField = (field: ValidationField, value: string) => {
    if (field === 'autoclave_serial') {
      return Boolean(value) && !serialNumbers.has(value)
    } else if (field === 'autoclave_name') {
      return Boolean(value) && !autoclaveNames.has(value)
    } else {
      return Boolean(value)
    }
  }

  const validateField = (field: ValidationField, value: string) => {
    const cleanValue = value.trim().toLowerCase()
    
    setFormErrors(prev => {
      const isValid = isValidField(field, cleanValue)
      const message = getValidationMessage(field, cleanValue)

      const validation = { isValid, message }

      return { ...prev, [field]: validation }
    })
  };

  const autoclaveFrequencies = getAutoclaveFrequencies()

  const hasRequiredFields = Boolean(formData.autoclave_name)
    && Boolean(formData.autoclave_serial) 
    && Boolean(formData.autoclave_brand)
    && Boolean(formData.autoclave_model)

  const disableSave = !hasRequiredFields
    || !formErrors.autoclave_serial.isValid
    || !formErrors.autoclave_name.isValid
    || loading

  const autoclaveTypeName = getAutoclaveTypeName(autoclave, autoclaveTypeById)
  const nextAutoclaveTypeName = getAutoclaveTypeName(formData, autoclaveTypeById)

  const handleClose = () => setOpenModal({ open: false, autoclave: null })

  return (
    <Dialog open={openModal.open} onClose={handleClose} fullWidth>
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>{autoclave?.autoclave_id ? 'Edit' : 'Add'} Autoclave</StyledDialogTitle>
      <StyledDialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "4px" }}>
          <img style={{ width: "100px" }} src={autoclaveImage} alt="autoclave image" />
        </Box>
        <TextField
          label="Autoclave Name"
          placeholder="required"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          error={!formErrors.autoclave_name.isValid}
          helperText={formErrors.autoclave_name.message}
          value={formData.autoclave_name}
          onChange={(e) => handleEditChange('autoclave_name', e.target.value)}
        />
        <TextField
          label="Serial/Sterilizer #"
          placeholder="required"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          error={!formErrors.autoclave_serial.isValid}
          helperText={formErrors.autoclave_serial.message}
          value={formData.autoclave_serial ?? ''}
          onChange={(e) => handleEditChange('autoclave_serial', e.target.value)}
        />
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={formData.autoclavetype_id ?? AutoclaveTypeId.Steam}
            onChange={(e) => handleEditChange('autoclavetype_id', e.target.value)}
          >
            {
              [
                AutoclaveTypeId.Steam,
                AutoclaveTypeId.DriHeat,
                AutoclaveTypeId.Chemical,
                AutoclaveTypeId.eTo
              ].map(typeId => {
                const key = `autoclave-type-id-${typeId}`

                return (
                  <MenuItem key={key} value={typeId}>
                    { autoclaveTypeById.get(typeId)?.autoclavetype_name ?? '' }
                  </MenuItem>                  
                )
              })
            }
          </Select>
        </FormControl>
        {
          hasChangedAutoclaveType && (
            <Box sx={{ pl: '12px', pr: '12px', pt: '4px' }} className="color-danger">
              Attention: You are trying to assign a box that is currently designated for { autoclaveTypeName } to a { nextAutoclaveTypeName } autoclave. Press Save if this is correct.
            </Box>
          )
        }
        <TextField
          label="Brand"
          placeholder="required"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          error={!formErrors.autoclave_brand.isValid}
          helperText={formErrors.autoclave_brand.message}          
          value={formData.autoclave_brand ?? ''}
          onChange={(e) => handleEditChange('autoclave_brand', e.target.value)}
        />
        <TextField
          label="Model"
          placeholder="required"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          error={!formErrors.autoclave_model.isValid}
          helperText={formErrors.autoclave_model.message}
          value={formData.autoclave_model ?? ''}
          onChange={(e) => handleEditChange('autoclave_model', e.target.value)}
        />
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            label="Frequency"
            value={[8, 10, 13, 12].includes(formData.autoclave_frequency ?? 0) ? formData.autoclave_frequency : 10}
            onChange={(e) => handleEditChange('autoclave_frequency', e.target.value)}
          >
            {
              Array
                .from(autoclaveFrequencies, ([value, frequency]) => ({ value, frequency}))
                .map(autoclaveFrequency => (
                  <MenuItem key={autoclaveFrequency.value} value={autoclaveFrequency.value}>
                    { autoclaveFrequency.frequency }
                  </MenuItem>
                  )
                )
            }
          </Select>
        </FormControl>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={disableSave}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default AddEditAutoclave;